import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import ReCAPTCHA from "react-google-recaptcha";

const CatalogueModal = ({ isOpen, onClose, modelName = "General" }) => {
    const { toast } = useToast();
    const [status, setStatus] = useState('idle'); // idle, submitting, success
    const [captchaToken, setCaptchaToken] = useState(null);
    const recaptchaRef = useRef(null);

    const schema = yup.object().shape({
        fullName: yup.string().required("Full Name is required"),
        mobileNumber: yup.string().required("Mobile Number is required"),
        email: yup.string().email("Invalid email").required("Email is required"),
    });

    const { register, handleSubmit, formState: { errors }, reset, setError } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            fullName: '',
            mobileNumber: '',
            email: ''
        }
    });

    const handleCaptchaChange = (token) => {
        setCaptchaToken(token);
    };

    const onSubmit = async (data) => {
        const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
        if (recaptchaSiteKey && !captchaToken) {
            toast({
                title: "Validation Error",
                description: "Please complete the ReCAPTCHA verification.",
                variant: "destructive",
            });
            return;
        }

        setStatus('submitting');
        try {
            const formData = new FormData();
            formData.append('fullName', data.fullName);
            formData.append('email', data.email);
            formData.append('phone', data.mobileNumber);
            formData.append('recaptcha_token', captchaToken);
            // formData.append('model', modelName); 

            const response = await axios.post('https://admin.baicoman.com/api/catalougue-request-leads', formData);

            if (response.status === 200 || response.status === 201) {
                setStatus('success');
                setTimeout(() => {
                    onClose();
                    setStatus('idle');
                    reset();
                    if (recaptchaSiteKey && recaptchaRef.current) {
                        recaptchaRef.current.reset();
                    }
                    setCaptchaToken(null);
                }, 2000);
            }
        } catch (error) {
            console.error(error);
            if (error.response && error.response.data && error.response.data.errors) {
                const serverErrors = error.response.data.errors;
                Object.keys(serverErrors).forEach((field) => {
                    setError(field, {
                        type: "server",
                        message: serverErrors[field][0],
                    });
                });
                const errorList = Object.values(serverErrors)
                    .flat()
                    .join("\n");

                toast({
                    title: "Validation Error",
                    description: errorList,
                    variant: "destructive",
                });
            } else {
                toast({
                    title: "Error",
                    description: "Something went wrong. Please try again.",
                    variant: "destructive",
                });
            }
            setStatus('idle');
        }
    };

    // Using Portal to ensure modal is always on top and correctly centered relative to viewport
    return typeof document !== 'undefined' ? ReactDOM.createPortal(
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] transition-opacity"
                    />

                    {/* Modal */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, x: '-50%', y: '-40%' }}
                        animate={{ opacity: 1, scale: 1, x: '-50%', y: '-50%' }}
                        exit={{ opacity: 0, scale: 0.95, x: '-50%', y: '-40%' }}
                        className="fixed left-1/2 top-1/2 w-full max-w-md bg-white rounded-2xl shadow-2xl z-[10000] overflow-hidden"
                    >
                        {/* Header */}
                        <div className="bg-red-600 p-6 flex justify-between items-center text-white">
                            <h3 className="text-xl font-bold uppercase tracking-wide">Request Brochure</h3>
                            <button
                                onClick={onClose}
                                className="p-1 hover:bg-white/20 rounded-full transition-colors"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="p-8">
                            {status === 'success' ? (
                                <div className="text-center py-8">
                                    <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <CheckCircle2 size={32} />
                                    </div>
                                    <h4 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h4>
                                    <p className="text-gray-600">The brochure for <span className="font-semibold">{modelName}</span> has been sent to your email.</p>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                                    <div>
                                        <p className="text-gray-600 mb-4 font-light">
                                            Enter your details below to receive the detailed brochure for <span className="font-semibold">{modelName}</span> directly in your inbox.
                                        </p>
                                        <div className="space-y-4">
                                            <div className="space-y-2">
                                                <label htmlFor="fullName" className="text-sm font-medium text-gray-700">Full Name</label>
                                                <input
                                                    id="fullName"
                                                    type="text"
                                                    {...register("fullName")}
                                                    placeholder="John Doe"
                                                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-600 focus:border-transparent outline-none transition-all placeholder:text-gray-400"
                                                />
                                                {errors.fullName && <p className="text-red-500 text-xs">{errors.fullName.message}</p>}
                                            </div>
                                            <div className="space-y-2">
                                                <label htmlFor="mobileNumber" className="text-sm font-medium text-gray-700">Mobile Number</label>
                                                <input
                                                    id="mobileNumber"
                                                    type="tel"
                                                    {...register("mobileNumber")}
                                                    placeholder="+968 12345678"
                                                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-600 focus:border-transparent outline-none transition-all placeholder:text-gray-400"
                                                />
                                                {errors.mobileNumber && <p className="text-red-500 text-xs">{errors.mobileNumber.message}</p>}
                                            </div>
                                            <div className="space-y-2">
                                                <label htmlFor="email" className="text-sm font-medium text-gray-700">Email Address</label>
                                                <input
                                                    id="email"
                                                    type="email"
                                                    {...register("email")}
                                                    placeholder="you@example.com"
                                                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-600 focus:border-transparent outline-none transition-all placeholder:text-gray-400"
                                                />
                                                {errors.email && <p className="text-red-500 text-xs">{errors.email.message}</p>}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex flex-col gap-4">
                                        {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
                                            <div className="flex justify-center">
                                                <div className="w-full flex flex-col items-center gap-2">
                                                    <ReCAPTCHA
                                                        ref={recaptchaRef}
                                                        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                                                        onChange={handleCaptchaChange}
                                                    />
                                                    {errors.recaptcha_token && <p className="text-red-500 text-xs">{errors.recaptcha_token.message}</p>}
                                                </div>
                                            </div>
                                        )}
                                        <Button
                                            type="submit"
                                            disabled={status === 'submitting'}
                                            className="w-full bg-red-600 hover:bg-red-700 text-white rounded-lg py-4 font-bold text-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
                                        >
                                            {status === 'submitting' ? (
                                                <Loader2 className="w-5 h-5 animate-spin" />
                                            ) : (
                                                <>Send Brochure <Send size={20} /></>
                                            )}
                                        </Button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>,
        document.body
    ) : null;
};

export default CatalogueModal;
